import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class SizePopupDialogue extends UcoastEl {
	static htmlSelector = 'size-popup-dialogue'
	static selectors = {
		inner: '[data-uc-size-popup-inner]',
		closeButton: '[data-uc-size-popup-close]',
	}

	private clickOutsideHandler?: (e: MouseEvent) => void
	private hasShownOnce = false
	private transformedAncestor?: HTMLElement

	constructor() {
		super()

		this.addEventListener('keyup', (evt) => {
			if (evt.code === 'Escape') this.close()
		})
		this.addEventListener('click', (evt) => {
			if (evt.target === this) this.close()
		})

		const closeButtons = q.rl(SizePopupDialogue.selectors.closeButton, this)
		closeButtons.forEach((button) => {
			button.addEventListener('click', this.close.bind(this))
		})

		this.clickOutsideHandler = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			if (!this.contains(target) && this.classList.contains('active')) {
				this.close()
			}
		}
	}

	show(anchorElement: HTMLElement) {
		if (!anchorElement) return

		// Only show popup once per page load
		if (this.hasShownOnce) return

		this.hasShownOnce = true

		const isMobile = window.innerWidth < 750
		const isStickyAtc = anchorElement.classList.contains('product-sticky-atc__variant-button')
		const stickyAtcContainer = anchorElement.closest('product-sticky-atc') as HTMLElement

		let slideDirection: 'left' | 'right' = 'left'

		if (isStickyAtc && stickyAtcContainer) {
			if (isMobile) {
				this.style.position = 'fixed'
				// Keep mobile layout controlled by CSS bottom-sheet styles.
				this.style.top = '0'
				this.style.left = '0'
				this.style.right = '0'
				this.style.bottom = '0'
				this.style.transform = ''
				slideDirection = 'left'
			} else {
				this.style.position = 'fixed'
				const rect = anchorElement.getBoundingClientRect()
				const viewportWidth = window.innerWidth
				
				this.style.visibility = 'hidden'
				this.style.pointerEvents = 'none'
				this.style.display = 'block'
				
				const popupRect = this.getBoundingClientRect()
				const popupWidth = popupRect.width
				const popupHeight = popupRect.height
				
				let left = rect.left - popupWidth - 8
				let top = rect.top + (rect.height - popupHeight) / 2
				
				if (left < 20) {
					left = rect.right + 8
					slideDirection = 'right'
				}
				
				if (left + popupWidth > viewportWidth) {
					left = viewportWidth - popupWidth - 20
				}
				
				this.style.top = `${top}px`
				this.style.left = `${left}px`
				this.style.right = 'auto'
				this.style.bottom = 'auto'
				this.style.transform = ''
			}
		} else {
			const viewportWidth = window.innerWidth
			const viewportHeight = window.innerHeight

			this.style.display = 'block'
			this.style.visibility = 'hidden'
			this.style.pointerEvents = 'none'
			this.style.transform = ''

			if (isMobile) {
				this.style.position = 'fixed'
				// Keep mobile layout controlled by CSS bottom-sheet styles.
				this.style.top = '0'
				this.style.left = '0'
				this.style.right = '0'
				this.style.bottom = '0'
				this.style.transform = ''
				slideDirection = 'left'
			} else {
				this.style.position = 'absolute'
				
				const popupRect = this.getBoundingClientRect()
				const popupWidth = popupRect.width
				const popupHeight = popupRect.height

				const rect = anchorElement.getBoundingClientRect()
				let top = rect.bottom + 8
				let left = rect.left

				if (top + popupHeight > viewportHeight) {
					top = rect.top - popupHeight - 8
					if (top < 20) {
						top = 20
					}
				}

				if (left + popupWidth > viewportWidth) {
					left = viewportWidth - popupWidth - 20
					slideDirection = 'right'
				}

				if (left < 20) {
					left = 20
				}

				if (left > rect.right) {
					slideDirection = 'right'
				}

					this.style.position = 'absolute'
					this.style.top = `${top}px`
					this.style.left = `${left}px`
					this.style.right = 'auto'
					this.style.bottom = 'auto'
			}
		}

		this.setAttribute('data-slide-direction', slideDirection)

		this.style.visibility = ''
		this.style.pointerEvents = ''
		if (isMobile) {
			this.transformedAncestor = this.closest('.product__info-wrapper') as HTMLElement | null || undefined
			this.transformedAncestor?.classList.add('size-popup-dialogue-parent-open')
			document.body.classList.add('overflow-hidden')
		}
		this.classList.add('active')

		if (this.clickOutsideHandler) {
			setTimeout(() => {
				document.addEventListener('click', this.clickOutsideHandler!)
			}, 0)
		}

		window.TsDOM.trapFocus(this, q.rs('[role="dialog"]', this))
	}

	close() {
		this.classList.remove('active')
		this.transformedAncestor?.classList.remove('size-popup-dialogue-parent-open')
		this.transformedAncestor = undefined
		document.body.classList.remove('overflow-hidden')
		if (this.clickOutsideHandler) {
			document.removeEventListener('click', this.clickOutsideHandler)
		}
		window.TsDOM.removeTrapFocus()
	}

	reset() {
		this.hasShownOnce = false
		this.close()
	}

	/**
	 * Find the SizePopupDialogue instance for the MainProduct section
	 * containing the given element.
	 */
	static findForSection(element: HTMLElement): SizePopupDialogue | null {
		const sectionEl = element.closest('section[id^="MainProduct-"]')
		const sectionIdAttr = sectionEl?.getAttribute('id') || ''
		const sectionId = sectionIdAttr.replace('shopify-section-', '').replace('MainProduct-', '')
		if (!sectionId) return null

		return document.querySelector<SizePopupDialogue>(`#SizePopupDialogue-${sectionId}`)
	}
}
